import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useGetFORoomssQuery } from '../redux/services/foroomsApi';
import {
  useGetFOEActiveLinkRoomsQuery,
  useInsertFOELinkRoomsMutation,
  useUpdateFOELinkRoomsMutation,
  useDeleteFOELinkRoomsMutation,
} from '../redux/services/foelinkroomsApi';
import { ShowToast } from '../Common/common';
import { buildLinkRoomsPayload } from '../utils/foActionUtils';

import { usePhoneticText } from '../utils/transliterate/usePhoneticInput';
import T from '../Components/T';
import { tp } from '../utils/translateText';
export const LinkRooms = ({ navigation, route }: any) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;

  const initialRoomId = route?.params?.room ? Number(route.params.room) : 0;
  const initialCheckinId = route?.params?.checkinId ? Number(route.params.checkinId) : 0;

  const { data: roomsData = [], isLoading: isLoadingRooms, refetch: refetchRooms } =
    useGetFORoomssQuery(cid);
  const { data: activeLinkRooms = [], isLoading: isLoadingLinks, refetch: refetchLinks } =
    useGetFOEActiveLinkRoomsQuery(cid, { skip: !cid });

  const [insertFOELinkRooms, { isLoading: isInserting }] = useInsertFOELinkRoomsMutation();
  const [updateFOELinkRooms, { isLoading: isUpdating }] = useUpdateFOELinkRoomsMutation();
  const [deleteFOELinkRooms, { isLoading: isDeleting }] = useDeleteFOELinkRoomsMutation();

  const isSaving = isInserting || isUpdating || isDeleting;

  // Occupied rooms only
  const occupiedRooms = useMemo(() => {
    return (roomsData || []).filter((r: any) => r.rm_status === 'O');
  }, [roomsData]);

  const [masterRoomId, setMasterRoomId] = useState<number>(initialRoomId);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const ph = usePhoneticText();
  const [remarks, setRemarks] = useState<string>('');
  const [activeLinkId, setActiveLinkId] = useState<number>(0);

  // Group active link rooms by lr_id
  const linkGroups = useMemo(() => {
    if (!activeLinkRooms || !Array.isArray(activeLinkRooms) || activeLinkRooms.length === 0) {
      return [];
    }

    const groupMap = new Map<
      number,
      {
        lr_id: number;
        lr_room: number;
        lr_checkin: number;
        lr_narr: string;
        members: { ci_id: number; rm_id: number }[];
      }
    >();

    for (const row of activeLinkRooms) {
      const lrId = Number(row.lr_id);
      if (!groupMap.has(lrId)) {
        groupMap.set(lrId, {
          lr_id: lrId,
          lr_room: Number(row.lr_room),
          lr_checkin: Number(row.lr_checkin),
          lr_narr: row.lr_narr || '',
          members: [
            { ci_id: Number(row.lr_checkin), rm_id: Number(row.lr_room) },
          ],
        });
      }

      const grp = groupMap.get(lrId)!;
      const childRoom = Number(row.ld_room);
      const childCheckin = Number(row.ld_checkin);
      if (childRoom > 0 && !grp.members.some(m => m.rm_id === childRoom)) {
        grp.members.push({ ci_id: childCheckin, rm_id: childRoom });
      }
    }

    return Array.from(groupMap.values());
  }, [activeLinkRooms]);

  // Set of all room IDs currently linked in any group
  const allLinkedRoomIds = useMemo(() => {
    const set = new Set<number>();
    if (activeLinkRooms && Array.isArray(activeLinkRooms)) {
      for (const link of activeLinkRooms) {
        if (link.lr_room) set.add(Number(link.lr_room));
        if (link.ld_room) set.add(Number(link.ld_room));
      }
    }
    return set;
  }, [activeLinkRooms]);

  // Initialize master room if none passed
  useEffect(() => {
    if (masterRoomId === 0 && occupiedRooms.length > 0) {
      setMasterRoomId(occupiedRooms[0].rm_id);
    }
  }, [occupiedRooms, masterRoomId]);

  // When master room changes, detect whether it is already in a link group
  useEffect(() => {
    if (masterRoomId <= 0) return;

    const foundGroup = linkGroups.find(g =>
      g.members.some(m => m.rm_id === masterRoomId),
    );

    if (foundGroup) {
      setActiveLinkId(foundGroup.lr_id);
      setRemarks(foundGroup.lr_narr || '');
      const peerKeys = foundGroup.members
        .filter(m => m.rm_id !== masterRoomId)
        .map(m => `${m.ci_id}.${m.rm_id}`);
      setCheckedKeys(peerKeys);
    } else {
      setActiveLinkId(0);
      setRemarks('');
      setCheckedKeys([]);
    }
  }, [masterRoomId, linkGroups]);

  const masterRoom = occupiedRooms.find((r: any) => r.rm_id === masterRoomId);

  // Peer rooms list (other occupied rooms)
  const peerRooms = useMemo(() => {
    return occupiedRooms.filter((r: any) => r.rm_id !== masterRoomId);
  }, [occupiedRooms, masterRoomId]);

  const handleToggleRoom = (key: string) => {
    if (checkedKeys.includes(key)) {
      setCheckedKeys(checkedKeys.filter(k => k !== key));
    } else {
      setCheckedKeys([...checkedKeys, key]);
    }
  };

  const handleUnlink = () => {
    if (!activeLinkId) return;

    Alert.alert(
      tp("Unlink Rooms"),
      `Are you sure you want to unlink room ${masterRoom?.rm_name || ''} and its linked rooms?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Unlink',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteFOELinkRooms({ lr_id: activeLinkId }).unwrap();
              ShowToast('Rooms unlinked successfully', 'success');
              refetchLinks();
              refetchRooms();
              navigation.goBack();
            } catch (error: any) {
              const msg = error?.data?.message || error?.message || 'Failed to unlink';
              ShowToast(typeof msg === 'string' ? msg : 'Failed to unlink', 'error');
            }
          },
        },
      ],
    );
  };

  const handleSave = async () => {
    if (checkedKeys.length === 0) {
      if (activeLinkId > 0) {
        handleUnlink();
        return;
      }
      ShowToast('Please select at least one room to link', 'warning');
      return;
    }

    if (!masterRoom) {
      ShowToast('Please select a master room', 'warning');
      return;
    }

    const payload = buildLinkRoomsPayload({
      cid,
      masterRoom: {
        rm_id: masterRoom.rm_id,
        ci_id: masterRoom.ci_id || initialCheckinId,
      },
      checkedRoomKeys: checkedKeys,
      activeLinkId,
      remarks,
      user: gdata.user,
    });

    try {
      if (activeLinkId > 0) {
        await updateFOELinkRooms(payload).unwrap();
        ShowToast('Linked rooms updated successfully', 'success');
      } else {
        await insertFOELinkRooms(payload).unwrap();
        ShowToast('Rooms linked successfully', 'success');
      }
      refetchLinks();
      refetchRooms();
      navigation.goBack();
    } catch (error: any) {
      const msg =
        error?.data?.message || error?.data || error?.message || 'Failed to save linked rooms';
      ShowToast(typeof msg === 'string' ? msg : 'Failed to save linked rooms', 'error');
    }
  };

  if (isLoadingRooms || isLoadingLinks) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#4f46e5" />
        <Text style={{ marginTop: 10, color: '#6b7280' }}><T>Loading rooms...</T></Text>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      {/* Header Bar */}
      <View style={styles.headerBar}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}>
          <FontAwesome name="arrow-left" size={18} color="#111827" />
        </TouchableOpacity>
        <Text style={styles.screenTitle}>
          {activeLinkId > 0 ? 'Edit Link Rooms' : 'Link Rooms'}
        </Text>
        {activeLinkId > 0 ? (
          <TouchableOpacity
            onPress={handleUnlink}
            disabled={isSaving}
            style={styles.headerUnlinkBtn}>
            <FontAwesome name="chain-broken" size={16} color="#dc2626" />
            <Text style={styles.headerUnlinkText}><T>Unlink</T></Text>
          </TouchableOpacity>
        ) : (
          <View style={{ width: 40 }} />
        )}
      </View>

      <ScrollView
        contentContainerStyle={styles.contentContainer}
        keyboardShouldPersistTaps="handled">
        {/* Linked Notice Banner */}
        {activeLinkId > 0 && (
          <View style={styles.linkedNoticeCard}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
              <FontAwesome name="link" size={18} color="#4f46e5" style={{ marginRight: 10 }} />
              <View style={{ flex: 1 }}>
                <Text style={styles.linkedNoticeTitle}><T>Currently Linked</T></Text>
                <Text style={styles.linkedNoticeDesc}>
                  <T>Linked with</T> {checkedKeys.length} <T>room(s). Uncheck rooms to update, or tap Unlink to clear all links.</T>
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Master Room Selection */}
        <View style={styles.card}>
          <Text style={styles.cardSectionTitle}><T>Master Room (Primary Folio)</T></Text>
          <View style={styles.pickerWrapper}>
            <Picker
              selectedValue={masterRoomId}
              onValueChange={v => setMasterRoomId(Number(v))}
              dropdownIconColor="#111827"
              style={styles.picker}>
              {occupiedRooms.map((r: any) => (
                <Picker.Item
                  key={r.rm_id}
                  label={`Room ${r.rm_name} - ${r.ab_name || 'Guest'} (#${r.ci_id})`}
                  value={r.rm_id}
                />
              ))}
            </Picker>
          </View>
        </View>

        {/* Peer Rooms Checklist */}
        <View style={styles.card}>
          <View style={styles.peerHeaderRow}>
            <Text style={styles.cardSectionTitle}><T>Select Rooms to Link</T></Text>
            <Text style={styles.peerCountText}>
              {checkedKeys.length} <T>selected</T>
            </Text>
          </View>

          {peerRooms.length === 0 ? (
            <Text style={styles.emptyText}>
              <T>No other occupied rooms available to link.</T>
            </Text>
          ) : (
            peerRooms.map((r: any) => {
              const key = `${r.ci_id}.${r.rm_id}`;
              const isChecked = checkedKeys.includes(key);
              const isAlreadyLinked = allLinkedRoomIds.has(Number(r.rm_id));

              return (
                <TouchableOpacity
                  key={key}
                  style={[styles.peerRow, isChecked && styles.peerRowChecked]}
                  onPress={() => handleToggleRoom(key)}>
                  <FontAwesome
                    name={isChecked ? 'check-square' : 'square-o'}
                    size={22}
                    color={isChecked ? '#4f46e5' : '#9ca3af'}
                    style={{ marginRight: 12 }}
                  />
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={styles.peerRoomName}><T>Room</T> {r.rm_name}</Text>
                      {r.rt_name ? (
                        <Text style={styles.peerRoomType}> ({r.rt_name})</Text>
                      ) : null}
                      {isAlreadyLinked && (
                        <View style={styles.linkedBadgePill}>
                          <Text style={styles.linkedBadgePillText}><T>Linked</T></Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.peerGuestName}>
                      {r.ab_name ? `Guest: ${r.ab_name}` : 'Folio #' + r.ci_id}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </View>

        {/* Remarks Input */}
        <View style={styles.card}>
          <Text style={styles.cardSectionTitle}><T>Remarks / Group Notes</T></Text>
          <TextInput
            style={styles.remarksInput}
            value={remarks}
            onChangeText={(t) => setRemarks(ph.onChange(t))}
            onBlur={() => setRemarks(ph.onBlur(remarks))}
            placeholder={tp("e.g. Family group, Corporate booking, Joint bill")}
            placeholderTextColor="#9ca3af"
            multiline
            numberOfLines={3}
          />
        </View>
      </ScrollView>

      {/* Bottom Actions Bar */}
      <View style={styles.bottomBar}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          disabled={isSaving}
          style={styles.cancelBtn}>
          <Text style={styles.cancelBtnText}><T>Cancel</T></Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={handleSave}
          disabled={isSaving}
          style={[styles.saveBtn, isSaving && styles.saveBtnDisabled]}>
          {isSaving ? (
            <ActivityIndicator size="small" color="#ffffff" />
          ) : (
            <>
              <FontAwesome name="check" size={16} color="#ffffff" style={{ marginRight: 6 }} />
              <Text style={styles.saveBtnText}>
                {activeLinkId > 0 ? 'Update Links' : 'Link Rooms'}
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#f1f5f9',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
  },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    elevation: 2,
  },
  backButton: {
    padding: 6,
  },
  screenTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0f172a',
  },
  headerUnlinkBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
    gap: 6,
  },
  headerUnlinkText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#dc2626',
  },
  contentContainer: {
    padding: 14,
    paddingBottom: 90,
  },
  linkedNoticeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#eef2ff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#c7d2fe',
    padding: 12,
    marginBottom: 12,
  },
  linkedNoticeTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#3730a3',
  },
  linkedNoticeDesc: {
    fontSize: 12,
    color: '#4338ca',
    marginTop: 2,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 14,
    marginBottom: 12,
  },
  cardSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 8,
  },
  pickerWrapper: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    height: 48,
    color: '#0f172a',
  },
  peerHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  peerCountText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4f46e5',
  },
  emptyText: {
    fontSize: 13,
    color: '#94a3b8',
    fontStyle: 'italic',
    paddingVertical: 8,
  },
  peerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    borderRadius: 6,
  },
  peerRowChecked: {
    backgroundColor: '#f5f3ff',
  },
  peerRoomName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0f172a',
  },
  peerRoomType: {
    fontSize: 13,
    color: '#64748b',
  },
  peerGuestName: {
    fontSize: 12,
    color: '#475569',
    marginTop: 2,
  },
  linkedBadgePill: {
    backgroundColor: '#e0e7ff',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 1,
    marginLeft: 6,
  },
  linkedBadgePillText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#4338ca',
  },
  remarksInput: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#0f172a',
    backgroundColor: '#ffffff',
    textAlignVertical: 'top',
    height: 75,
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  cancelBtn: {
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
  },
  saveBtn: {
    flexDirection: 'row',
    paddingHorizontal: 22,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#4f46e5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveBtnDisabled: {
    opacity: 0.6,
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
  },
});

export default LinkRooms;
